import { dirtySignOutEvent, doSignoutEvent, eventBus } from '../internal';
import ApplicationSwitcherObservable, { APP_DATA_LOADED, SUBSCRIBE_ALL, UPDATE_MODEL, USE_NOVA_APPSWITCHER } from './events/ApplicationSwitcherObservable';
import { BROADCAST, DO_NOVA_LOGOFF, DO_SEND_MESSAGE_TO_APP, GOTO_APP, LOGOFF, ORIGIN, SEND_MESSAGE_TO_APP, SET_DIRTY_MESSAGE_ENABLED, SET_LOGOFF_URL, TIMEOUT, UPDATE_APP_NAME, UPDATE_DIRTY_STATE, VERSION } from './messages/PostMessageCommand';
import { isApplicationBranch } from './model/ApplicationSwitcherModel';
export const SUBSCRIBE_ALL_TYPES = SUBSCRIBE_ALL;
class ApplicationSwitcherClient {
  observable = new ApplicationSwitcherObservable();
  model = {
    availableApplications: [],
    publicTypeSupportingApplications: new Map()
  };
  messageQueue = [];
  readyForMessages = false;
  appId = '';
  dirtyAppNames = '\n';
  constructor(config) {
    window.addEventListener('message', this.onMessage.bind(this));
    this.observable.subscribe(UPDATE_MODEL, () => this.updateModel());
    // If Nova appswitcher is being used, get dirty app names.
    this.observable.subscribe(USE_NOVA_APPSWITCHER, (_, dirtyAppsData) => {
      this.dirtyAppNames = dirtyAppsData.dirtyAppNames;
    });
    this.config = config;
    this.updateModel();
    eventBus.subscribe(doSignoutEvent, (_, eventData) => {
      if (eventData?.data.handled) {
        return;
      }
      if (eventData) {
        eventData.data.handled = true;
      }

      // If using Nova appswitcher and there are dirty apps, show Nova dirty sign out message and
      // do Nova logoff if confirmed. Otherwise, do default logoff.
      if (this.dirtyAppNames !== '\n') {
        eventBus.publish(dirtySignOutEvent, {
          data: {
            dirtyAppNames: this.dirtyAppNames,
            onDiscardAndExit: () => this.doNovaLogoff()
          }
        });
      } else {
        this.logoff();
      }
    });

    // Initial app already has window.onpopstate set, so this will only be added to
    // subsequent apps.
    if (!window.onpopstate) {
      window.onpopstate = this.onPopState.bind(this);
    }
  }
  onPopState() {
    if (!this.appId) return;
    this.gotoApp(this.appId, window.location.href, true);
  }
  onMessage(event) {
    if (window.location.origin !== event.origin) return;
    let message;
    if (typeof event.data === 'string') {
      try {
        message = JSON.parse(event.data);
      } catch (e) {
        return;
      }
    } else {
      message = event.data;
    }
    if (message === null || message.version === undefined) return;
    switch (message.command) {
      case DO_SEND_MESSAGE_TO_APP:
        this.processMessage(message);
        break;
    }
  }
  processMessage(message) {
    if (this.readyForMessages) {
      this.observable.fire(message.type, message.data);
    } else {
      this.messageQueue.push(message);
    }
  }
  async updateModel() {
    this.model.availableApplications = await this.config.applicationService.getAvailableApps();
    this.model.publicTypeSupportingApplications = await this.config.applicationService.getPublicTypeSupportingApps();
    this.observable.fire(APP_DATA_LOADED);
  }
  gotoApp(appId, url, isCompliant, options = {}) {
    const message = {
      version: VERSION,
      command: GOTO_APP,
      appId,
      url,
      isCompliant,
      options
    };
    this.sendCommandToMain(message);
  }
  updateAppName(appId, appName) {
    const message = {
      version: VERSION,
      command: UPDATE_APP_NAME,
      appId,
      appName
    };
    this.sendCommandToMain(message);
  }
  sendCommandToMain(message) {
    top?.window.postMessage(JSON.stringify(message), ORIGIN);
  }
  sendMessageToApp(appId, type, data) {
    const message = {
      version: VERSION,
      command: SEND_MESSAGE_TO_APP,
      appId,
      type,
      data
    };
    this.sendCommandToMain(message);
  }
  subscribe(type, fn, context) {
    this.observable.subscribe(type, fn, context);
  }
  unsubscribe(type, fn, context) {
    this.observable.unsubscribe(type, fn, context);
  }
  broadcastMessage(type, data = {}) {
    const message = {
      version: VERSION,
      command: BROADCAST,
      type,
      data
    };
    this.sendCommandToMain(message);
  }
  setLogoffUrl(logoffUrl) {
    const message = {
      version: VERSION,
      command: SET_LOGOFF_URL,
      logoffUrl
    };
    this.sendCommandToMain(message);
  }
  logoff() {
    const message = {
      version: VERSION,
      command: LOGOFF
    };
    this.sendCommandToMain(message);
  }
  doNovaLogoff() {
    const message = {
      version: VERSION,
      command: DO_NOVA_LOGOFF
    };
    this.sendCommandToMain(message);
  }
  setDirtyMessageEnabled(allowDirtyMessage) {
    const setDirtyMessageEnabledMessage = {
      version: VERSION,
      command: SET_DIRTY_MESSAGE_ENABLED,
      allowDirtyMessage
    };
    this.sendCommandToMain(setDirtyMessageEnabledMessage);
  }
  timeout(url) {
    const timeoutMessage = {
      version: VERSION,
      command: TIMEOUT,
      url
    };
    this.sendCommandToMain(timeoutMessage);
  }
  updateDirtyFlag(appId, dirtyFlag) {
    const message = {
      version: VERSION,
      command: UPDATE_DIRTY_STATE,
      appId,
      dirtyFlag
    };
    this.sendCommandToMain(message);
  }
  registerApp(appId, url, appName) {
    this.appId = appId;
    this.gotoApp(appId, url, true);
    this.updateAppName(appId, appName);
    this.readyForMessages = true;
    this.flushMessageQueue();
  }
  getModel() {
    return this.model;
  }
  openObject(objectType, objectInfo, openType, preferredAppId) {
    const appId = this.getAppIdForType(objectType, preferredAppId);
    if (!appId) {
      return;
    }
    const app = this.getAvailableApp(appId);
    if (!app) {
      return;
    }
    this.gotoApp(app.appId, app.uri, app.appSwitcherCompliance);
    this.sendMessageToApp(app.appId, openType, {
      ...objectInfo,
      directive: app.directiveId,
      targetPublicType: objectType
    });
  }
  getAppIdForType(objectType, preferredAppId) {
    const type = this.model.publicTypeSupportingApplications.get(objectType);
    if (!type) {
      return null;
    }
    let appId = null;
    if (preferredAppId) {
      const filteredTypes = type.filter(appTypeInfo => appTypeInfo.appId === preferredAppId);
      if (filteredTypes.length > 0) {
        appId = preferredAppId;
      }
    }
    if (!appId && type.length > 0) {
      appId = type[0].appId;
    }
    return appId;
  }
  getAvailableApp(appId) {
    let app = null;
    const appTree = this.model.availableApplications;
    for (const appNode of appTree) {
      if (isApplicationBranch(appNode)) {
        for (const appLeaf of appNode.children) {
          if (appLeaf.appId === appId) {
            app = appLeaf;
          }
        }
      } else if (appNode.appId === appId) {
        app = appNode;
      }
    }
    return app;
  }
  flushMessageQueue() {
    for (const message of this.messageQueue) {
      this.observable.fire(message.type, message.data);
    }
    this.messageQueue = [];
  }
}
export default ApplicationSwitcherClient;
//# sourceMappingURL=ApplicationSwitcherClient.js.map